import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, CheckCircle, XCircle, Loader2, RefreshCw, History,
  Phone, Mail, Building2, Clock
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// ADMIN - Demandes de services managés (ex: Page Facebook)
// Un humain doit approuver avant que le client puisse s'auto-servir.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

const STATUS_BADGES = {
  pending:  { label: 'En attente', color: 'bg-yellow-500/20 text-yellow-300' },
  approved: { label: 'Approuvée ✓', color: 'bg-green-500/20 text-green-300' },
  rejected: { label: 'Rejetée', color: 'bg-red-500/20 text-red-300' },
};

export default function ServiceRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(async (history = showHistory) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/service-requests?status=${history ? 'all' : 'pending'}`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setRequests(data.requests);
    } finally { setLoading(false); }
  }, [showHistory]);

  useEffect(() => { load(); }, [load]);

  const approve = async (req) => {
    let body = {};
    if (req.service === 'facebook_page') {
      const maxPagesStr = window.prompt(
        `Approuver la demande de ${req.full_name} ?\n\nCombien de pages Facebook peut-il connecter ?`, '1'
      );
      if (maxPagesStr === null) return;
      body = { max_pages: parseInt(maxPagesStr, 10) || 1 };
    } else {
      if (!window.confirm(`Approuver la demande WhatsApp de ${req.full_name} ?\n\nVous devrez ensuite créer la connexion manuellement dans l'onglet WhatsApp.`)) return;
    }
    setBusyId(req.id);
    setMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/service-requests/${req.id}/approve`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body)
      });
      const data = await res.json();
      setMessage({ type: data.success ? 'ok' : 'error', text: data.message });
      load();
    } finally { setBusyId(null); }
  };

  const reject = async (req) => {
    const note = window.prompt('Raison du rejet (visible en interne) :', 'Non retenu');
    if (note === null) return;
    setBusyId(req.id);
    try {
      await fetch(`${API_URL}/api/bot/service-requests/${req.id}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ note })
      });
      load();
    } finally { setBusyId(null); }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
            <Users className="w-8 h-8 text-blue-400" />
            Demandes de Services
            {pendingCount > 0 && (
              <span className="px-3 py-1 rounded-full bg-yellow-500 text-black text-sm font-bold animate-pulse">
                {pendingCount} à traiter
              </span>
            )}
          </h1>
          <p className="text-sm text-blue-200">
            Service Page Facebook géré par un humain — approuvez pour donner l'accès au client
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
              showHistory ? 'bg-purple-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
            }`}
          >
            <History className="w-4 h-4" /> {showHistory ? 'Tout' : 'En attente'}
          </button>
          <button onClick={() => load()} className="p-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-2xl flex items-center gap-3 text-sm ${
          message.type === 'ok'
            ? 'bg-green-500/10 border border-green-500/30 text-green-300'
            : 'bg-red-500/10 border border-red-500/30 text-red-300'
        }`}>
          {message.type === 'ok' ? <CheckCircle className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-10 h-10 text-blue-400 animate-spin mx-auto" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center">
          <CheckCircle className="w-12 h-12 text-green-500/50 mx-auto mb-3" />
          <p className="text-gray-400">
            {showHistory ? 'Aucune demande.' : 'Aucune demande en attente 🎉'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map(req => {
            const st = STATUS_BADGES[req.status] || STATUS_BADGES.pending;
            return (
              <div key={req.id} className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-5">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className="text-white font-semibold text-sm">{req.full_name}</span>
                      <span className="px-2 py-0.5 rounded-full text-xs bg-blue-500/20 text-blue-300">{req.service_label}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                      <span className="text-gray-500 text-xs flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {req.created_at ? new Date(req.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400 flex-wrap mb-1">
                      <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {req.email}</span>
                      {req.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {req.phone}</span>}
                      {req.business_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {req.business_name}</span>}
                    </div>
                    {req.message && <p className="text-blue-100 text-xs italic mt-1">« {req.message} »</p>}
                    {req.note && <p className="text-gray-500 text-xs mt-1">{req.note}</p>}
                  </div>

                  {req.status === 'pending' && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => approve(req)}
                        disabled={busyId === req.id}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-500 text-white font-bold text-sm transition-all disabled:opacity-50"
                      >
                        {busyId === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        Approuver
                      </button>
                      <button
                        onClick={() => reject(req)}
                        disabled={busyId === req.id}
                        className="px-3 py-2.5 rounded-xl bg-red-500/20 text-red-300 hover:bg-red-500/30 text-sm transition-all disabled:opacity-50"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
